import { ChangeEvent, useEffect, useState } from "react";
import { updateGeneral } from "@/dispatchers/generalDispatchers";
import { useTitanApp } from "@/hooks/useTitanApp";
import { showError } from "@/components/ToastedMessage/ToastedMessage";

interface GeneralSettings {
  organizationName: string;
  organizationAddress?: string;
  phoneNumber?: string;
  email?: string;
  organizationLogo: string;
  primaryColor: string;
  secondaryColor: string;
  brandingEnabled: boolean;
  logoUrl?: string;
  companyID?: string;
  companyOwnerUserId?: string;
  companyOwnerEmailAddress?: string;
  companyOwnerFirstName?: string;
  companyOwnerGender?: string | null;
  companyOwnerLastName?: string;
  userImage?: string;
  companyTheme?: string;
  welcomeEmail?: string;
  companyType?: number;
  description?: string;
  gender?: number;
  salesForceId?: string;
  companySubscriptionType?: number;
  companySubscriptionStatus?: number;
}

export const defaultGeneralSettings: GeneralSettings = {
  organizationName: "",
  organizationAddress: "",
  phoneNumber: "",
  email: "",
  organizationLogo: "",
  primaryColor: "",
  secondaryColor: "",
  brandingEnabled: true,
  logoUrl: "",
  companyID: "",
  companyOwnerUserId: "",
  companyOwnerEmailAddress: "",
  companyOwnerFirstName: "",
  companyOwnerGender: null,
  companyOwnerLastName: "",
  userImage: "",
  companyTheme: "",
  welcomeEmail: "",
  companyType: 1,
  description: "",
  gender: 0,
  salesForceId: "",
  companySubscriptionType: 0,
  companySubscriptionStatus: 1,
};

export const useGeneralSettingsData = () => {
  const { appState, generalsState } = useTitanApp();
  const { isGeneralLoading, generalData } = generalsState || {};
  const { darkMode } = appState || {};
  const { features } = appState || {};
  const { ifSettingMinimal } = features || {};
  const [logoError, setLogoError] = useState<string | null>(null);

  const [selectedMenuItem, setSelectedMenuItem] = useState("General");
  const [showPrimaryPicker, setShowPrimaryPicker] = useState(false);
  const [showSecondaryPicker, setShowSecondaryPicker] = useState(false);
  const isDarkMode = darkMode ?? false;

  const [settings, setSettings] = useState<GeneralSettings>({
    organizationName: generalData?.companyName || "",
    organizationAddress: generalData?.companyAddress || "",
    phoneNumber: generalData?.contactNumber || "",
    email: generalData?.companyOwnerEmailAddress || "",
    organizationLogo: generalData?.companyLogo || "",
    primaryColor: generalData?.primaryColor || "",
    secondaryColor: generalData?.secondaryColor || "",
    brandingEnabled: true,
    logoUrl: generalData?.companyLogo || "",
    companyID: generalData?.companyID || "",
    companyOwnerUserId: generalData?.companyOwnerUserId || "",
    companyOwnerEmailAddress: generalData?.companyOwnerEmailAddress || "",
    companyOwnerFirstName: generalData?.companyOwnerFirstName || "",
    companyOwnerGender: generalData?.companyOwnerGender || null,
    companyOwnerLastName: generalData?.companyOwnerLastName || "",
    userImage: generalData?.userImage || "",
    companyTheme: generalData?.companyTheme || "",
    welcomeEmail: generalData?.welcomeEmail || "",
    companyType: generalData?.companyType || 1,
    description: generalData?.description || "",
    gender: generalData?.gender || 0,
    salesForceId: generalData?.salesForceId,
    companySubscriptionType: generalData?.companySubscriptionType,
    companySubscriptionStatus: generalData?.companySubscriptionStatus,
  });

  const logoPreview = settings.organizationLogo || null;

  useEffect(() => {
    if (generalData) {
      setSettings({
        organizationName: generalData?.companyName ?? "",
        organizationAddress: generalData?.companyAddress ?? "",
        phoneNumber: generalData?.contactNumber ?? "",
        email: generalData?.companyOwnerEmailAddress ?? "",
        organizationLogo: generalData?.companyLogo ?? "",
        primaryColor: generalData?.primaryColor ?? "",
        secondaryColor: generalData?.secondaryColor ?? "",
        brandingEnabled: generalData?.brandingEnabled ?? true,
        logoUrl: generalData?.companyLogo ?? "",
        companyID: generalData?.companyID ?? "",
        companyOwnerUserId: generalData?.companyOwnerUserId ?? "",
        companyOwnerEmailAddress: generalData?.companyOwnerEmailAddress ?? "",
        companyOwnerFirstName: generalData?.companyOwnerFirstName ?? "",
        companyOwnerGender: generalData?.companyOwnerGender ?? null,
        companyOwnerLastName: generalData?.companyOwnerLastName ?? "",
        userImage: generalData?.userImage ?? "",
        companyTheme: generalData?.companyTheme ?? "",
        welcomeEmail: generalData?.welcomeEmail ?? "",
        companyType: generalData?.companyType ?? 1,
        description: generalData?.description ?? "",
        gender: generalData?.gender ?? 0,
        salesForceId: generalData?.salesForceId,
        companySubscriptionType: generalData?.companySubscriptionType,
        companySubscriptionStatus: generalData?.companySubscriptionStatus,
      });
    }
  }, [generalData]);

  const handleInputChange =
    (field: keyof GeneralSettings) =>
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setSettings((prev) => ({
        ...prev,
        [field]: e.target.value,
      }));
    };

  const handleColorChange =
    (field: "primaryColor" | "secondaryColor") => (color: { hex: string }) => {
      setSettings((prev) => ({ ...prev, [field]: color.hex }));
    };

  const handleLogoChange = (imageList: any[]) => {
    if (imageList?.length > 0) {
      const logoUrl = imageList[0]?.data_url;
      setSettings((prev) => ({
        ...prev,
        organizationLogo: logoUrl,
      }));
    } else {
      setSettings((prev) => ({
        ...prev,
        organizationLogo: "",
      }));
    }
  };

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const MAX_LOGO_SIZE = 1 * 1024 * 1024; // 1 MB

    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_LOGO_SIZE) {
      setLogoError("Logo size must be 1 MB or less");
      event.target.value = "";
      return;
    }

    setLogoError(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result;
      if (typeof result === "string") {
        handleLogoChange([{ data_url: result }]);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleToggleBranding = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSettings((prev) => ({
      ...prev,
      brandingEnabled: e.target.checked,
    }));
  };

  const saveGeneralSettings = async (mergedData: any) => {
    const companyData = {
      companyID: mergedData.companyID || "",
      companyName: mergedData.organizationName,
      companyAddress: mergedData.organizationAddress || "",
      companyOwnerUserId: mergedData.companyOwnerUserId || "",
      companyOwnerEmailAddress: mergedData.email || "",
      companyOwnerFirstName: mergedData.companyOwnerFirstName || "",
      companyOwnerGender: mergedData.companyOwnerGender || null,
      companyOwnerLastName: mergedData.companyOwnerLastName || "",
      userImage: mergedData.userImage || "",
      companyLogo: mergedData.organizationLogo || "",
      companyTheme: mergedData.companyTheme || "",
      contactNumber: mergedData.phoneNumber || "",
      welcomeEmail: mergedData.welcomeEmail || "",
      companyType: mergedData.companyType || 1,
      primaryColor: mergedData.primaryColor || "",
      secondaryColor: mergedData.secondaryColor || "",
      description: mergedData.description || "",
      gender: mergedData.gender || 0,
      salesForceId: mergedData.salesForceId,
      companySubscriptionType: mergedData.companySubscriptionType,
      companySubscriptionStatus: mergedData.companySubscriptionStatus,
    };

    try {
      const response = await updateGeneral(companyData);
      if (!response?.success) {
        showError("Could not update general settings.", true, false);
      }
    } catch (error) {
      showError("Update failed.", true, false);
    }
  };

  return {
    isDarkMode,
    settings,
    logoPreview,
    setSettings,
    selectedMenuItem,
    showPrimaryPicker,
    showSecondaryPicker,
    isGeneralLoading,
    setSelectedMenuItem,
    setShowPrimaryPicker,
    setShowSecondaryPicker,
    handleInputChange,
    handleColorChange,
    handleLogoChange,
    handleLogoUpload,
    handleToggleBranding,
    saveGeneralSettings,
    logoError,
  };
};
